import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fullName, timeAgo } from "@/lib/format";
import { toast } from "sonner";

export default function Conversation() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();

  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async (scroll = false) => {
    try {
      const { data } = await api.get(`/messages/thread/${userId}`);
      setThread(data);
      if (scroll) {
        requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }));
      }
    } catch (e) {
      toast.error("Could not load conversation");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial load + polling every 5s
  useEffect(() => {
    setLoading(true);
    load(true);
    const t = setInterval(() => load(false), 5000);
    return () => clearInterval(t);
  }, [load]);

  // Autoscroll to bottom on new message count change
  useEffect(() => {
    if (thread) requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }));
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e && e.preventDefault();
    const text = content.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/thread/${userId}`, { content: text });
      setThread((t) => t ? { ...t, messages: [...t.messages, data] } : t);
      setContent("");
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  if (loading || !thread) {
    return <div className="text-center py-12 text-muted-foreground" data-testid="conversation-loading">Loading conversation…</div>;
  }

  const other = thread.other_user;

  return (
    <div className="flex flex-col" style={{ minHeight: "70vh" }} data-testid="conversation-view">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-border cornell-shadow p-3 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/messages")} className="text-neutral-600" data-testid="conversation-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Link to={`/profile/${other.id}`} className="flex items-center gap-3 flex-1 min-w-0" data-testid="conversation-header-user">
          <UserAvatar user={other} size={40} />
          <div className="min-w-0">
            <div className="font-semibold truncate">{fullName(other)}</div>
            <div className="text-xs text-muted-foreground truncate">{other.college} · Class of {other.grad_year}</div>
          </div>
        </Link>
      </div>

      {/* Privacy note */}
      <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground bg-white border border-border rounded-xl px-3 py-2" data-testid="privacy-note">
        <Lock className="w-3.5 h-3.5 shrink-0 text-[#B31B1B]" />
        <span>Messages stay within CCNEO Connect. No emails or phone numbers are shared.</span>
      </div>

      {/* Messages */}
      <div className="flex-1 mt-3 space-y-2" data-testid="conversation-messages">
        {thread.messages.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-8" data-testid="conversation-empty">
            Say hi to {other.first_name}!
          </div>
        )}
        {thread.messages.map((m, idx) => {
          const mine = m.from_id === me?.id;
          const prev = thread.messages[idx - 1];
          const showTime = !prev || (new Date(m.created_at) - new Date(prev.created_at)) > 5 * 60 * 1000;
          return (
            <div key={m.id} className={mine ? "flex justify-end" : "flex justify-start"}>
              <div className="max-w-[78%]">
                {showTime && (
                  <div className={`text-[10px] text-muted-foreground mb-0.5 ${mine ? "text-right" : "text-left"}`}>
                    {timeAgo(m.created_at)}
                  </div>
                )}
                <div
                  className={`px-3.5 py-2 rounded-2xl text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                    mine
                      ? "bg-[#B31B1B] text-white rounded-br-sm"
                      : "bg-white border border-border text-neutral-800 rounded-bl-sm"
                  }`}
                  data-testid={`message-${m.id}`}
                >
                  {m.content}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      {thread.can_send ? (
        <form onSubmit={send} className="mt-3 flex gap-2 items-end bg-white border border-border rounded-2xl p-2 sticky bottom-24 lg:bottom-4" data-testid="conversation-composer">
          <Textarea
            rows={1}
            placeholder={`Message ${other.first_name}…`}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="min-h-[40px] resize-none border-0 shadow-none focus-visible:ring-0"
            maxLength={2000}
            data-testid="conversation-input"
          />
          <Button
            type="submit"
            disabled={!content.trim() || sending}
            className="bg-[#B31B1B] hover:bg-[#8f1616] rounded-full shrink-0"
            data-testid="conversation-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      ) : (
        <div className="mt-3 text-center text-sm text-muted-foreground bg-white border border-border rounded-2xl p-4" data-testid="conversation-blocked">
          {other.first_name} is not accepting direct messages right now.
        </div>
      )}
    </div>
  );
}
